import { useEffect } from 'react';
import buildCanonicalUrl, { canonicalBase } from '../utils/canonicalUrl';
import { seoConfig } from '../utils/seoConfig';

const SEO = ({ 
  title = 'InitX Technologies - Initiating Innovation with Expertise', 
  description = 'Software development, cloud solutions & IT consulting for startups and enterprises. Build reliable, scalable digital products with InitX.',
  keywords = 'InitX Technologies, Initiating Innovation with Expertise, technology solutions, AI development, cloud computing, web development, mobile app development, digital transformation, software engineering, tech consulting, enterprise solutions, startup technology, machine learning, data analytics, cybersecurity, DevOps, API development, microservices, blockchain development, IoT solutions, automation, tech innovation',
  image = '/InitX_Logo.png',
  url,
  type = 'website',
  siteName = 'InitX Technologies',
  noindex = false,
  breadcrumbs = null
}) => {
  useEffect(() => {
    // Update document title
    document.title = title;
    
    // Helper function to update meta tags
    const updateMetaTag = (name, content, property = false) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let element = document.querySelector(selector);
      
      if (!element) {
        element = document.createElement('meta');
        if (property) {
          element.setAttribute('property', name);
        } else {
          element.setAttribute('name', name);
        }
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    const canonicalUrl = buildCanonicalUrl(url);
    const normalizedSiteName = siteName || seoConfig?.company?.name || 'InitX Technologies';
    const imageUrl = image.startsWith('http')
      ? image
      : `${canonicalBase}${image.startsWith('/') ? image : `/${image}`}`;
    const company = seoConfig?.company || {};
    const social = seoConfig?.social || {};

    // Advanced SEO Meta Tags for Higher Rankings
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('author', company.name || 'InitX Technologies');
    updateMetaTag('publisher', company.name || 'InitX Technologies');
    updateMetaTag('copyright', '© 2026 InitX Technologies');
    updateMetaTag('language', 'English');
    updateMetaTag('revisit-after', '7 days');
    updateMetaTag('distribution', 'global');
    updateMetaTag('rating', 'general');
    const robotsValue = noindex
      ? 'noindex, nofollow'
      : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1';
    updateMetaTag('robots', robotsValue);
    updateMetaTag('googlebot', robotsValue);
    updateMetaTag('bingbot', robotsValue);
    
    // Geographic and Business Meta Tags
    updateMetaTag('geo.region', 'IN-BR');
    updateMetaTag('geo.placename', 'Sasaram');
    updateMetaTag('geo.region', 'IN-UP');
    updateMetaTag('geo.placename', 'Lucknow');
    
    // Content Classification
    updateMetaTag('content-language', 'en-US');
    updateMetaTag('audience', 'all');
    updateMetaTag('classification', 'Technology Solutions, Software Development, AI Services');
    updateMetaTag('category', 'Technology');
    updateMetaTag('coverage', 'Worldwide');
    
    // Technical SEO
    updateMetaTag('theme-color', '#8a2be2');
    updateMetaTag('msapplication-TileColor', '#8a2be2');
    updateMetaTag('format-detection', 'telephone=no');
    
    // Enhanced Open Graph Tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:image', imageUrl, true);
    updateMetaTag('og:image:secure_url', imageUrl, true);
    updateMetaTag('og:image:type', 'image/png', true);
    updateMetaTag('og:image:width', '512', true);
    updateMetaTag('og:image:height', '512', true);
    updateMetaTag('og:image:alt', `${normalizedSiteName} Logo`, true);
    updateMetaTag('og:url', canonicalUrl, true);
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:site_name', normalizedSiteName, true);
    updateMetaTag('og:locale', 'en_US', true);
    updateMetaTag('og:updated_time', new Date().toISOString(), true);
    
    // Enhanced Twitter Card Tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:site', '@initxtech');
    updateMetaTag('twitter:creator', '@initxtech');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', imageUrl);
    updateMetaTag('twitter:image:alt', `${normalizedSiteName} Logo`);
    updateMetaTag('twitter:domain', 'initx.online');
    updateMetaTag('twitter:url', canonicalUrl);
    
    // LinkedIn and Pinterest
    updateMetaTag('linkedin:owner', 'initx-technologies');
    updateMetaTag('pinterest-rich-pin', 'true');
    
    // Mobile optimization
    updateMetaTag('mobile-web-app-capable', 'yes');
    updateMetaTag('apple-mobile-web-app-capable', 'yes');
    updateMetaTag('apple-mobile-web-app-status-bar-style', 'black-translucent');
    updateMetaTag('apple-mobile-web-app-title', normalizedSiteName);
    updateMetaTag('application-name', normalizedSiteName);
    
    // Security and Performance
    updateMetaTag('referrer', 'strict-origin-when-cross-origin');
    
    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = canonicalUrl;
    
    // Advanced Structured Data for Tech Company
    const structuredData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "@id": `${canonicalBase}/#organization`,
          "name": company.name || "InitX Technologies",
          "alternateName": ["InitX"],
          "description": description,
          "url": canonicalBase,
          "logo": {
            "@type": "ImageObject",
            "url": `${canonicalBase}/InitX_Logo.png`,
            "width": 512,
            "height": 512
          },
          "image": `${canonicalBase}/InitX_Logo.png`,
          "foundingDate": company.founded || "2021",
          "founders": [
            {
              "@type": "Person",
              "name": "InitX Founder"
            }
          ],
          "contactPoint": [
            {
              "@type": "ContactPoint",
              "telephone": company.phone || "+91-8469718513",
              "contactType": "customer service",
              "availableLanguage": ["English", "Spanish", "French"],
              "email": company.email || "contact@initx.online"
            },
            {
              "@type": "ContactPoint",
              "telephone": company.phone || "+91-8469718513",
              "contactType": "sales",
              "availableLanguage": "English",
              "email": company.email || "contact@initx.online"
            }
          ],
          "address": [
            // Registered Office - Hidden
            // {
            //   "@type": "PostalAddress",
            //   "name": "Registered Office",
            //   "addressLocality": "Sasaram",
            //   "addressRegion": "Bihar",
            //   "postalCode": "821115",
            //   "addressCountry": "IN"
            // },
            {
              "@type": "PostalAddress",
              "name": "Corporate Office",
              "addressLocality": "Lucknow",
              "addressRegion": "Uttar Pradesh",
              "postalCode": "226013",
              "addressCountry": "IN"
            }
          ],
          "sameAs": [
            social.facebook ? `https://facebook.com/${social.facebook}` : "https://facebook.com/initxtech",
            social.twitter ? `https://twitter.com/${social.twitter.replace('@', '')}` : "https://twitter.com/initxtech",
            social.linkedin ? `https://linkedin.com/company/${social.linkedin}` : "https://linkedin.com/company/initx-technologies",
            social.github ? `https://github.com/${social.github}` : "https://github.com/initxtech",
            social.youtube ? `https://youtube.com/${social.youtube}` : "https://youtube.com/c/initxtech",
            social.instagram ? `https://instagram.com/${social.instagram}` : "https://instagram.com/initxtech"
          ],
          "knowsAbout": [
            "Artificial Intelligence",
            "Machine Learning",
            "Cloud Computing",
            "Web Development",
            "Mobile App Development",
            "Digital Transformation",
            "Software Engineering",
            "DevOps",
            "Cybersecurity",
            "Data Analytics",
            "Blockchain",
            "IoT Solutions"
          ],
          "areaServed": "IN"
        },
        {
          "@type": "WebSite",
          "@id": `${canonicalBase}/#website`,
          "url": canonicalBase,
          "name": company.name || "InitX Technologies",
          "alternateName": [company.shortName || "InitX", normalizedSiteName],
          "description": seoConfig?.defaultDescription || "InitX Technologies provides custom software development, web and mobile engineering, and IT consulting services for startups and enterprises.",
          "publisher": {
            "@id": `${canonicalBase}/#organization`
          },
          "inLanguage": "en-US"
        },
        {
          "@type": "WebPage",
          "@id": `${canonicalUrl}#webpage`,
          "url": canonicalUrl,
          "name": title,
          "description": description,
          "isPartOf": {
            "@id": `${canonicalBase}/#website`
          }
        },
        {
          "@type": "Service",
          "name": "Technology Solutions",
          "description": "Comprehensive technology services including AI development, cloud infrastructure, and digital transformation",
          "provider": {
            "@id": `${canonicalBase}/#organization`
          },
          "serviceType": "Technology Consulting",
          "areaServed": "Worldwide",
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Technology Services",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "AI Development",
                  "description": "Custom AI and machine learning solutions"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Cloud Solutions",
                  "description": "Scalable cloud infrastructure and migration services"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Web Development",
                  "description": "Modern web applications and platforms"
                }
              }
            ]
          }
        }
      ]
    };
    
    // Update structured data
    let scriptElement = document.querySelector('script[data-schema="main"]');
    if (!scriptElement) {
      scriptElement = document.createElement('script');
      scriptElement.type = 'application/ld+json';
      scriptElement.setAttribute('data-schema', 'main');
      document.head.appendChild(scriptElement);
    }
    scriptElement.textContent = JSON.stringify(structuredData);

    if (Array.isArray(breadcrumbs) && breadcrumbs.length > 0) {
      const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((crumb, index) => {
          const rawUrl = crumb.url || '/';
          const itemUrl = rawUrl.startsWith('http')
            ? rawUrl
            : `${canonicalBase}${rawUrl.startsWith('/') ? rawUrl : `/${rawUrl}`}`;
          return {
            "@type": "ListItem",
            "position": index + 1,
            "name": crumb.name,
            "item": itemUrl
          };
        })
      };

      let breadcrumbScript = document.querySelector('script[data-schema="breadcrumbs"]');
      if (!breadcrumbScript) {
        breadcrumbScript = document.createElement('script');
        breadcrumbScript.type = 'application/ld+json';
        breadcrumbScript.setAttribute('data-schema', 'breadcrumbs');
        document.head.appendChild(breadcrumbScript);
      }
      breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);
    }
    
    // Add hreflang for international SEO
    const hreflangs = [
      { lang: 'en', href: canonicalUrl },
      { lang: 'x-default', href: canonicalUrl }
    ];
    
    hreflangs.forEach(({ lang, href }) => {
      let hreflangLink = document.querySelector(`link[hreflang="${lang}"]`);
      if (!hreflangLink) {
        hreflangLink = document.createElement('link');
        hreflangLink.rel = 'alternate';
        hreflangLink.hreflang = lang;
        document.head.appendChild(hreflangLink);
      }
      hreflangLink.href = href;
    });
    
  }, [title, description, keywords, image, url, type, siteName, noindex, breadcrumbs]);

  return null;
};

export default SEO;
